/**
 * Agent — a logical voice agent within a Pinecall connection.
 *
 * Created via `pc.agent("my-agent", config?)`.
 * Each agent owns channels (phone, webrtc) and receives events
 * independently from other agents on the same connection.
 *
 * The old _handleEvent() 200-line switch is gone. Dispatch handlers now
 * call typed _apply* methods and _emitWire directly.
 */

import { TypedEventBus } from "../kernel/event-bus.js";
import { Call } from "./call.js";
import type { CallInit } from "./call.js";
import { RingingCall } from "./ringing-call.js";
import { buildShortcutPayload } from "../protocol/shortcuts.js";
import type { Turn } from "./turn.js";
import type { AgentConfig, ChannelConfig, WhatsAppChannelConfig } from "../config/agent.js";
import type { Tool } from "../tool.js";
import { memorySearch, memoryGet, memoryForget, type MemoryHit, type MemoryContact, type MemoryFact } from "../api/memory.js";
import type { Skill, SkillConfig } from "../skill.js";
import { skill as makeSkill } from "../skill.js";
import type { TokenResponse, TokenScopeOptions } from "../api/tokens.js";
import type {
    CallStartedEvent,
    SpeechStartedEvent,
    SpeechEndedEvent,
    UserSpeakingEvent,
    UserMessageEvent,
    EagerTurnEvent,
    TurnPauseEvent,
    TurnEndEvent,
    TurnResumedEvent,
    TurnContinuedEvent,
    BotSpeakingEvent,
    BotWordEvent,
    BotFinishedEvent,
    BotInterruptedEvent,
    MessageConfirmedEvent,
    ReplyRejectedEvent,
    AudioMetricsEvent,
    SessionTimeoutEvent,
    ToolCallEvent,
} from "../protocol/events.js";

// ─── Agent events ────────────────────────────────────────────────────────

/** One applied memory op, as emitted on `memory.ops`. */
export type MemoryOp =
    | { op: "add"; id: string; kind: string; text: string; confidence: number; valid_from: string; evidence?: string }
    | { op: "update"; id: string; supersedes: string; kind: string; text: string; confidence: number; valid_from: string; evidence?: string }
    | { op: "delete"; id: string; kind?: string; text?: string; reason?: string };

/** The `memory.ops` payload — the same JSON the call log and the DataChannel carry. */
export interface MemoryOpsEvent {
    contact: string;
    call_id: string;
    turn: number;
    /** True on the end-of-call consolidation pass. */
    final: boolean;
    ops: MemoryOp[];
    memory: { revision: number; path: string };
    model: string;
    latency_ms: number;
}

/** `agent.memory` — read what the server remembers. */
export interface AgentMemory {
    /** Semantic + lexical search. With `contact`, only that contact; without, across every contact of the agent. */
    search(query: string, opts?: { contact?: string | null; k?: number }): Promise<MemoryHit[]>;
    /** Every fact held about a contact, plus the regenerated memory.md. */
    get(contact: string): Promise<MemoryContact>;
    /** The right to be forgotten: facts, view and index entries, gone. */
    forget(contact: string): Promise<boolean>;
}
export type { MemoryHit, MemoryContact, MemoryFact };

export interface AgentEvents {
    [key: string]: (...args: any[]) => void;

    // Lifecycle
    ready: () => void;
    "call.started": (call: Call) => void;
    /**
     * Memory learned or revised something about the contact of a session —
     * see `AgentConfig.memory`. `ops` is what was APPLIED (final ids, validity),
     * not what was asked; `final` marks the end-of-call pass.
     */
    "memory.ops": (ops: MemoryOpsEvent, call: Call | undefined) => void;
    "call.ended": (call: Call, reason: string) => void;
    "call.ringing": (call: RingingCall) => void;

    // Speech events
    "speech.started": (event: SpeechStartedEvent, call: Call) => void;
    "speech.ended": (event: SpeechEndedEvent, call: Call) => void;
    "user.speaking": (event: UserSpeakingEvent, call: Call) => void;
    "user.message": (event: UserMessageEvent, call: Call) => void;

    // Turn events
    "eager.turn": (turn: Turn, call: Call) => void;
    "turn.pause": (event: TurnPauseEvent, call: Call) => void;
    "turn.end": (turn: Turn, call: Call) => void;
    "turn.resumed": (event: TurnResumedEvent, call: Call) => void;
    "turn.continued": (event: TurnContinuedEvent, call: Call) => void;

    // Bot events
    "bot.speaking": (event: BotSpeakingEvent, call: Call) => void;
    "bot.word": (event: BotWordEvent, call: Call) => void;
    "bot.finished": (event: BotFinishedEvent, call: Call) => void;
    "bot.interrupted": (event: BotInterruptedEvent, call: Call) => void;

    // Confirmations
    "message.confirmed": (event: MessageConfirmedEvent, call: Call) => void;
    "reply.rejected": (event: ReplyRejectedEvent, call: Call) => void;

    // Analysis
    "audio.metrics": (event: AudioMetricsEvent, call: Call) => void;

    // Session limits
    "session.idleWarning": (event: any, call: Call) => void;
    "session.timeout": (event: SessionTimeoutEvent, call: Call) => void;

    // Keypad — phone only; a browser has no keypad to press.
    "call.dtmf_received": (event: import("../protocol/events.js").CallDtmfReceivedEvent, call: Call) => void;

    // LLM / Tool calls
    "llm.toolCall": (event: ToolCallEvent, call: Call) => void;

    // Skills
    "skill.loaded": (event: import("./call.js").SkillEvent, call: Call) => void;
    "skill.unloaded": (event: import("./call.js").SkillEvent, call: Call) => void;

    // Channel events
    "channel.added": (type: string, ref: string) => void;
    "channel.configured": (ref: string) => void;
    "channel.removed": (ref: string) => void;

    // WhatsApp events
    "whatsapp.message": (event: Record<string, unknown>) => void;
    "whatsapp.response": (event: Record<string, unknown>) => void;
    "whatsapp.status": (event: Record<string, unknown>) => void;
    "whatsapp.sessionStarted": (session: import("./wa-session.js").WhatsAppSession) => void;
    "whatsapp.sessionEnded": (event: Record<string, unknown>) => void;

    // Human-in-the-loop
    "session.paused": (event: { sessionId?: string; contact?: string }) => void;
    "session.resumed": (event: { sessionId?: string; contact?: string }) => void;

    // Pre-turn hook — see CallEvents["call.preparing"]. Returning a promise
    // holds the turn until it settles.
    "call.preparing": (call: Call) => void | Promise<unknown>;
    "call.preparingTimeout": (
        event: import("./call.js").PreparingTimeoutEvent,
        call: Call,
    ) => void;
}

// ─── Agent class ─────────────────────────────────────────────────────────

export class Agent extends TypedEventBus<AgentEvents> {
    readonly id: string;
    /** Human-readable display name. Defaults to id. */
    name: string;
    #config: AgentConfig;
    #tools: Tool[] = [];
    /** Declared skills (latent on the server until activated). */
    #skills: Skill[] = [];
    #calls = new Map<string, Call>();
    #sendRaw: (data: Record<string, unknown>) => void;
    #serverReady = false;
    #pendingQueue: Record<string, unknown>[] = [];
    /** Tracks registered channels for re-registration on reconnect. */
    #channels = new Map<string, { type: string; ref?: string; config?: ChannelConfig }>();
    /** Tracked dev callers for re-registration on reconnect. */
    #devCallers: string[] = [];
    /** @internal Reference to parent Pinecall client (for createToken). */
    #client: {
        createToken: (channel: "webrtc" | "chat" | "stream", agentId: string, metadata?: Record<string, unknown>, opts?: TokenScopeOptions) => Promise<TokenResponse>;
        memoryApi?: { apiKey: string; apiUrl: string };
    } | null = null;
    /** True once the SERVER acknowledged this agent (`agent.created`/`agent.resumed`). */
    #registered = false;
    #readyPromise!: Promise<void>;
    #readyResolve!: () => void;
    #readyReject!: (err: Error) => void;

    /** @internal — created by Pinecall.agent() */
    constructor(
        id: string,
        config: AgentConfig,
        send: (data: Record<string, unknown>) => void,
    ) {
        super();
        this.id = id;
        this.name = id;
        this.#config = config;
        this.#tools = config.tools ?? [];
        this.#skills = config.skills ?? [];
        this.#sendRaw = send;
        this.#armReady();
    }

    #armReady(): void {
        this.#readyPromise = new Promise<void>((resolve, reject) => {
            this.#readyResolve = resolve;
            this.#readyReject = reject;
        });
        // Awaiting `ready` is optional, so a rejection must never surface as an
        // unhandledRejection crash in a process that never looked at it.
        this.#readyPromise.catch(() => {});
    }

    /**
     * Send a raw protocol message. Buffers if the agent isn't server-ready yet.
     *
     * Prefer high-level methods like `call.toolResult()`, `call.say()`,
     * `call.reply()`, `agent.setDevCallers()` etc. Use `send()` only
     * as an escape hatch for protocol-level access.
     */
    send(data: Record<string, unknown>): void {
        if (this.#serverReady) {
            this.#sendRaw(data);
        } else {
            this.#pendingQueue.push(data);
        }
    }

    /** @internal Alias for backwards compat — use send() instead. */
    _send(data: Record<string, unknown>): void {
        this.send(data);
    }

    // ── Public getters ───────────────────────────────────────────────────

    /** All active calls for this agent. */
    get calls(): ReadonlyMap<string, Call> {
        return this.#calls;
    }

    /** Get a specific call by ID. */
    call(callId: string): Call | undefined {
        return this.#calls.get(callId);
    }

    /** Get the current agent config. */
    getConfig(): AgentConfig {
        return this.#config;
    }

    /**
     * True once the SERVER has acknowledged this agent's registration.
     *
     * `pc.agent()` returns synchronously — it only *queues* `agent.create` on
     * the socket. Until the server answers `agent.created`, the agent does not
     * exist server-side, so token mints and inbound routing 404 on it.
     */
    get registered(): boolean {
        return this.#registered;
    }

    /**
     * Resolves when the SERVER has acknowledged this agent's registration
     * (`agent.created` / `agent.resumed`) — NOT when `pc.agent()` returned.
     *
     * Await this before doing anything that requires the agent to exist
     * server-side (minting a chat/WebRTC token, dialing out). Rejects with
     * {@link AgentConflictError} if the registration is terminally refused.
     * Goes back to pending if the socket drops, and resolves again once the
     * reconnect re-registers the agent.
     *
     * @example
     * const agent = pc.agent("recepcion", { prompt });
     * await agent.ready;                       // server now knows it
     * const { token } = await agent.createToken("chat");
     */
    get ready(): Promise<void> {
        return this.#readyPromise;
    }

    // ── Channel management ───────────────────────────────────────────────

    /**
     * Register a phone number or SIP URI. Idempotent — calling again with the
     * same number updates its config.
     *
     * @example
     * agent.addPhoneNumber("+13186330963");
     * agent.addPhoneNumber("+34612345678", { ringing: true, voice: "elevenlabs/lucia" });
     * agent.addPhoneNumber("sip:bot@trunk.twilio.com");
     */
    addPhoneNumber(number: string, config?: ChannelConfig): void {
        this._addChannel("phone", number, config);
    }

    /**
     * Register a WhatsApp channel. Idempotent — calling again with the
     * same phoneNumberId updates its config.
     *
     * @example
     * agent.addWhatsapp({ phoneNumberId: "123", accessToken: "EAA..." });
     */
    addWhatsapp(config: WhatsAppChannelConfig): void {
        this._addChannel("whatsapp", config);
    }

    /**
     * Register the browser voice channel, so this agent can take WebRTC calls.
     *
     * Phone and WhatsApp had public methods and these two did not — the only
     * way in was `_addChannel`, which is internal. Anything outside this
     * package (a plugin, another @pinecall/* module) could not open a browser
     * channel without reaching past the public API.
     *
     * @example
     * agent.addWebrtc();
     */
    addWebrtc(config?: ChannelConfig): void {
        this._addChannel("webrtc", undefined, config);
    }

    /**
     * Register the text chat channel. Same reasoning as `addWebrtc()`.
     *
     * @example
     * agent.addChat();
     */
    addChat(config?: ChannelConfig): void {
        this._addChannel("chat", undefined, config);
    }

    /**
     * Remove a phone number or SIP URI.
     *
     * @example agent.removePhone("+13186330963");
     */
    removePhone(number: string): void {
        this.#channels.delete(number);
        this._send({ event: "channel.remove", agent_id: this.id, type: "phone", ref: number });
    }

    /**
     * Remove a WhatsApp channel by phoneNumberId.
     *
     * @example agent.removeWhatsapp("123");
     */
    removeWhatsapp(phoneNumberId: string): void {
        this.#channels.delete(phoneNumberId);
        this._send({ event: "channel.remove", agent_id: this.id, type: "whatsapp", ref: phoneNumberId });
    }

    /** @internal — used by client.ts and config processing. */
    _addChannel(type: "phone" | "webrtc" | "chat" | "whatsapp", ref?: string | WhatsAppChannelConfig, config?: ChannelConfig): void {
        // Validate phone numbers early (SIP URIs pass through)
        if (type === "phone" && typeof ref === "string" && ref && !ref.startsWith("sip:")) {
            const cleaned = ref.replace(/[\s\-()]/g, "");
            const normalized = cleaned.startsWith("+") ? cleaned : "+" + cleaned;
            const digits = normalized.slice(1);
            if (!/^\d+$/.test(digits) || digits.length < 7 || digits.length > 15) {
                throw new Error(`Invalid phone number "${ref}": must be E.164 format (+, 7-15 digits)`);
            }
        }

        // Track for re-registration on reconnect
        const key = (typeof ref === "string" ? ref : undefined) ?? type;
        this.#channels.set(key, { type, ref: typeof ref === "string" ? ref : undefined, config: typeof ref === "object" ? ref : config });

        // WhatsApp: ref is a WhatsAppChannelConfig object
        if (type === "whatsapp" && typeof ref === "object" && ref !== null) {
            const waConfig = ref as WhatsAppChannelConfig;
            const msg = {
                event: "channel.add",
                agent_id: this.id,
                type: "whatsapp",
                ref: waConfig.phoneNumberId,
                accessToken: waConfig.accessToken,
                ...(waConfig.verifyToken ? { verifyToken: waConfig.verifyToken } : {}),
                ...(waConfig.appSecret ? { appSecret: waConfig.appSecret } : {}),
                ...(waConfig.phone ? { phone: waConfig.phone } : {}),
                ...buildShortcutPayload(waConfig),
            };
            this._send(msg);
            return;
        }

        const msg = {
            event: "channel.add",
            agent_id: this.id,
            type,
            ...(typeof ref === "string" && ref ? { ref } : {}),
            ...buildShortcutPayload(config),
            ...(config?.ringing ? { ringing: true } : {}),
        };
        this._send(msg);
    }

    configureChannel(ref: string, config: ChannelConfig): void {
        this._send({
            event: "channel.configure",
            agent_id: this.id,
            ref,
            ...buildShortcutPayload(config),
        });
    }

    removeChannel(ref: string): void {
        this.#channels.delete(ref);
        this._send({
            event: "channel.remove",
            agent_id: this.id,
            ref,
        });
    }

    // ── Agent configuration ──────────────────────────────────────────────

    update(opts: AgentConfig): void {
        this.#config = { ...this.#config, ...opts };
        // Keep the executable universe in sync so auto-dispatch can run any
        // tool the LLM may now call (otherwise _getTools() goes stale and the
        // SDK replies "Unknown tool" to freshly hot-reloaded tools).
        if (opts.tools !== undefined) this.#tools = opts.tools;
        if (opts.skills !== undefined) this.#skills = opts.skills;
        this._send({
            event: "agent.configure",
            agent_id: this.id,
            ...buildShortcutPayload(opts),
        });
    }

    /**
     * Attach (or hot-reload) a single skill at runtime. The skill is sent to the
     * server and kept latent until activated (by the model, by `call.loadSkill`,
     * or immediately if `activation: "always"`).
     */
    skill(config: SkillConfig): Skill {
        const s = makeSkill(config);
        // Replace an existing skill with the same name, else append.
        const idx = this.#skills.findIndex((x) => x.name === s.name);
        if (idx >= 0) this.#skills[idx] = s;
        else this.#skills.push(s);
        this.#config = { ...this.#config, skills: this.#skills };
        this._send({
            event: "agent.configure",
            agent_id: this.id,
            skills: this.#skills.map((x) => x._toWire()),
        });
        return s;
    }

    /** @deprecated Use `agent.update()` instead. */
    configure(opts: AgentConfig): void {
        this.update(opts);
    }

    configureSession(sessionId: string, opts: ChannelConfig): void {
        this._send({
            event: "session.configure",
            agent_id: this.id,
            session_id: sessionId,
            ...buildShortcutPayload(opts),
        });
    }

    // ── Development ──────────────────────────────────────────────────────

    routeCallers(callers: string[]): void {
        this.#devCallers = callers;
        this.send({ event: "dev.config", callers });
    }

    // ── Token generation ─────────────────────────────────────────────────

    /**
     * Mint a short-lived browser token for this agent (webrtc / chat / stream).
     *
     * `metadata` (optional) is sealed into the signed token — trusted server-side
     * (the browser cannot forge it) and surfaced as `call.metadata` for tools.
     * Use for per-session identity (tenantId, userId, role).
     */
    async createToken(
        channel: "webrtc" | "chat" | "stream",
        metadata?: Record<string, unknown>,
        opts?: TokenScopeOptions,
    ): Promise<TokenResponse> {
        if (!this.#client) {
            throw new Error(
                "Cannot create token: agent is not connected to a Pinecall client. " +
                "Use pc.createToken(channel, agentId) instead.",
            );
        }
        return this.#client.createToken(channel, this.id, metadata, opts);
    }

    /** @internal Set the parent Pinecall client reference. */
    _setClient(client: {
        createToken: (channel: "webrtc" | "chat" | "stream", agentId: string, metadata?: Record<string, unknown>, opts?: TokenScopeOptions) => Promise<TokenResponse>;
        memoryApi?: { apiKey: string; apiUrl: string };
    }): void {
        this.#client = client;
    }

    // ── Memory ────────────────────────────────────────────────────────────

    /**
     * What this agent remembers about its contacts. Reads go to the server's
     * store over REST with the org's key — the agent need not be online.
     * See `AgentConfig.memory` for how facts get there.
     */
    get memory(): AgentMemory {
        const api = () => {
            const m = this.#client?.memoryApi;
            if (!m?.apiKey) throw new Error("agent.memory needs an API key (PINECALL_API_KEY / new Pinecall({ apiKey }))");
            return { ...m, agent: this.id };
        };
        return {
            search: (query, opts) => memorySearch(api(), query, opts),
            get: (contact) => memoryGet(api(), contact),
            forget: (contact) => memoryForget(api(), contact),
        };
    }

    // ── Dial ──────────────────────────────────────────────────────────────

    dial(options: {
        to: string;
        /** Caller ID. If omitted, uses the agent's only phone channel. */
        from?: string;
        greeting?: string;
        metadata?: Record<string, unknown>;
        config?: Record<string, unknown>;
        /**
         * When true, the server also detects the OTHER party's end-of-turn and
         * emits `turn.end` to this (initiating) side — so an automated caller
         * (e.g. a test/judge agent talking to another agent) knows when to
         * speak. Default false (a normal caller is a human and doesn't need it).
         */
        detectTurnEnd?: boolean;
    }): Promise<Call> {
        // Auto-resolve `from` if not provided
        let from = options.from;
        if (!from) {
            const phoneChannels: string[] = [];
            for (const [key, ch] of this.#channels) {
                if (ch.type === "phone" && ch.ref) phoneChannels.push(ch.ref);
            }
            if (phoneChannels.length === 0) {
                return Promise.reject(new Error(
                    "No phone numbers registered. Add one with `phoneNumber: \"+1...\"` or pass `from` explicitly.",
                ));
            }
            if (phoneChannels.length > 1) {
                return Promise.reject(new Error(
                    `Multiple phone channels registered (${phoneChannels.join(", ")}). Pass \`from\` to specify which one to use.`,
                ));
            }
            from = phoneChannels[0];
        }

        return new Promise<Call>((resolve, reject) => {
            let settled = false;
            const cleanup = () => {
                this.off("call.started", onStarted);
                this.off("call.ended", onEnded);
                this.off("error" as any, onError);
            };
            const onStarted = (call: Call) => {
                if (call.to === options.to || call.direction === "outbound") {
                    if (settled) return;
                    settled = true;
                    cleanup();
                    if (options.greeting) call.greeting = options.greeting;
                    resolve(call);
                }
            };
            const onEnded = (call: Call | null, reason: string) => {
                // Outbound call ended before connecting (busy, no-answer, failed, canceled)
                if (settled) return;
                settled = true;
                cleanup();
                reject(new Error(reason || "call_rejected"));
            };
            const onError = (err: Error) => {
                if (settled) return;
                settled = true;
                cleanup();
                reject(err);
            };
            this.on("call.started", onStarted);
            this.on("call.ended", onEnded);
            this.on("error" as any, onError);

            this._send({
                event: "call.dial",
                agent_id: this.id,
                to: options.to,
                from,
                ...(options.greeting ? { greeting: options.greeting } : {}),
                ...(options.metadata ? { metadata: options.metadata } : {}),
                ...(options.config ? { config: options.config } : {}),
                ...(options.detectTurnEnd ? { detect_turn_end: true } : {}),
            });

            setTimeout(() => {
                if (settled) return;
                settled = true;
                cleanup();
                reject(new Error("Dial timeout"));
            }, 30000);
        });
    }

    // ── Bridge (agent-to-agent voice) ─────────────────────────────────────

    /**
     * Place a VOICE call to ANOTHER Pinecall agent (no phone, no WebRTC).
     *
     * The server cross-wires the two agents' audio: this agent's TTS becomes the
     * target's incoming audio and vice-versa, so both run their real
     * STT/turn-detection/TTS pipelines. This agent is driven manually — speak
     * with `call.say()` and read the target via `user.message` / `turn.end`.
     * Typically the calling agent has no server-side LLM (it's puppeted by your
     * code), e.g. the `pinecall test` voice judge.
     *
     * @param target - The target agent's slug (must be online in the same org).
     */
    bridge(target: string, options: {
        greeting?: string;
        /** Detect the target's end-of-turn and emit `turn.end` to this side. Default true. */
        detectTurnEnd?: boolean;
        /** Per-call config override for THIS (calling) agent — voice, STT, language. */
        config?: Record<string, unknown>;
        /** Enable live listening / recording on the bridged call. */
        media?: { live?: boolean; recording?: boolean };
        metadata?: Record<string, unknown>;
    } = {}): Promise<Call> {
        return new Promise<Call>((resolve, reject) => {
            let settled = false;
            const cleanup = () => {
                this.off("call.started", onStarted);
                this.off("call.ended", onEnded);
                this.off("error" as any, onError);
            };
            const onStarted = (call: Call) => {
                if (settled) return;
                settled = true;
                cleanup();
                if (options.greeting) call.greeting = options.greeting;
                resolve(call);
            };
            const onEnded = (_call: Call | null, reason: string) => {
                if (settled) return;
                settled = true;
                cleanup();
                reject(new Error(reason || "bridge_rejected"));
            };
            const onError = (err: Error) => {
                if (settled) return;
                settled = true;
                cleanup();
                reject(err);
            };
            this.on("call.started", onStarted);
            this.on("call.ended", onEnded);
            this.on("error" as any, onError);

            this._send({
                event: "call.bridge",
                agent_id: this.id,
                target,
                ...(options.greeting ? { greeting: options.greeting } : {}),
                ...(options.config ? { config: options.config } : {}),
                ...(options.media ? { media: options.media } : {}),
                ...(options.metadata ? { metadata: options.metadata } : {}),
                detect_turn_end: options.detectTurnEnd !== false,
            });

            setTimeout(() => {
                if (settled) return;
                settled = true;
                cleanup();
                reject(new Error("Bridge timeout"));
            }, 30000);
        });
    }

    // ── Human-in-the-loop ─────────────────────────────────────────────────

    /**
     * Pause the AI agent. While paused, incoming messages are forwarded to
     * the SDK but the LLM does not generate responses — a human takes over.
     *
     * @param target - Session ID, `{ contact: "+34..." }`, or omit for global pause.
     */
    pause(target?: string | { contact: string }): void {
        const msg: Record<string, unknown> = {
            event: "session.pause",
            agent_id: this.id,
        };
        if (typeof target === "string") {
            msg.session_id = target;
        } else if (target && "contact" in target) {
            msg.contact = target.contact;
        }
        this.send(msg);
    }

    /**
     * Resume the AI agent after a pause.
     *
     * @param target - Session ID, `{ contact: "+34..." }`, or omit for global resume.
     */
    resume(target?: string | { contact: string }): void {
        const msg: Record<string, unknown> = {
            event: "session.resume",
            agent_id: this.id,
        };
        if (typeof target === "string") {
            msg.session_id = target;
        } else if (target && "contact" in target) {
            msg.contact = target.contact;
        }
        this.send(msg);
    }

    /**
     * Send a message as the human operator (not AI-generated).
     * Works while the session is paused — the message is sent through the
     * channel (WhatsApp, etc.) and added to LLM history for context.
     *
     * Pass `contact` (the customer's phone) alongside `sessionId` so the server
     * can still deliver via WhatsApp when the referenced session has already
     * expired (2h idle / 24h window GC) — it falls back to a direct channel
     * send for that contact instead of a silent no-op.
     */
    sendMessage(opts: { sessionId?: string; contact?: string; text: string }): void {
        this.send({
            event: "session.send",
            agent_id: this.id,
            ...(opts.sessionId ? { session_id: opts.sessionId } : {}),
            ...(opts.contact ? { contact: opts.contact } : {}),
            text: opts.text,
        });
    }

    // ── Dispatch-only API (friend methods) ───────────────────────────────

    /** @internal End all calls (on disconnect). */
    _endAllCalls(reason: string): void {
        for (const call of this.#calls.values()) {
            call._applyEnd(reason);
        }
        this.#calls.clear();
        this.#serverReady = false;
    }

    /** @internal Emit a typed event — used by dispatch handlers. */
    _emitWire<K extends keyof AgentEvents>(event: K, ...args: Parameters<AgentEvents[K]>): void {
        this.emit(event, ...args);
    }

    /**
     * @internal Run agent-level `call.preparing` handlers and hand back what
     * they returned, so async ones can be awaited before the turn is released.
     */
    _emitPreparing(call: Call): unknown[] {
        return this.emitCollect("call.preparing", call);
    }

    /** @internal True when the app is listening for the pre-turn hook. */
    _hasPreparingListener(): boolean {
        return this.listenerCount("call.preparing") > 0;
    }

    /**
     * @internal Build the `Call` for an inbound session.
     *
     * A seam, not a factory pattern for its own sake: a `PhoneLine` registers
     * under `line:<number>` so that every dispatch handler routes to it
     * unchanged, and this is the one place it has to differ — the object the
     * handler hands out is a `LineCall`, with `say`/`listen`/`ask`/`routeTo`.
     */
    _createCall(data: CallInit, send: (data: Record<string, unknown>) => void): Call {
        return new Call(data, send);
    }

    /** @internal Get a call by ID. */
    _getCall(callId: string): Call | undefined {
        return this.#calls.get(callId);
    }

    /** @internal Set a call in the registry. */
    _setCall(callId: string, call: Call): void {
        this.#calls.set(callId, call);
    }

    /** @internal Remove a call from the registry. */
    _deleteCall(callId: string): boolean {
        return this.#calls.delete(callId);
    }

    /** @internal Check if a call exists. */
    _hasCall(callId: string): boolean {
        return this.#calls.has(callId);
    }

    /** @internal Get channels map (for PHONE_IN_USE handling). */
    _getChannels(): Map<string, { type: string; ref?: string; config?: ChannelConfig }> {
        return this.#channels;
    }

    /**
     * @internal Get executable Tool objects for auto-dispatch.
     *
     * Returns the full executable universe — global tools plus every declared
     * skill's tools — regardless of which skills are currently active on the
     * server. Visibility to the LLM is decided server-side; execution must
     * always succeed, so we never want a "Unknown tool" for a latent skill.
     */
    _getTools(): Tool[] {
        if (this.#skills.length === 0) return this.#tools;
        const byName = new Map<string, Tool>();
        for (const t of this.#tools) byName.set(t.name, t);
        for (const s of this.#skills) for (const t of s.tools) byName.set(t.name, t);
        return [...byName.values()];
    }

    /** @internal Get declared skills. */
    _getSkills(): Skill[] {
        return this.#skills;
    }

    /** @internal Mark agent as server-ready and flush buffered messages. */
    _flushPending(): void {
        this.#serverReady = true;

        // Re-register all tracked channels (critical for reconnection)
        for (const [key, ch] of this.#channels) {
            // WhatsApp channels need special handling
            if (ch.type === "whatsapp" && ch.config) {
                const waConfig = ch.config as any;
                const msg = {
                    event: "channel.add",
                    agent_id: this.id,
                    type: "whatsapp",
                    ref: waConfig.phoneNumberId,
                    accessToken: waConfig.accessToken,
                    ...(waConfig.verifyToken ? { verifyToken: waConfig.verifyToken } : {}),
                    ...(waConfig.appSecret ? { appSecret: waConfig.appSecret } : {}),
                    ...buildShortcutPayload(waConfig),
                };
                this.#sendRaw(msg);
            } else {
                const msg = {
                    event: "channel.add",
                    agent_id: this.id,
                    type: ch.type,
                    ...(ch.ref ? { ref: ch.ref } : {}),
                    ...buildShortcutPayload(ch.config),
                    ...(ch.config?.ringing ? { ringing: true } : {}),
                };
                this.#sendRaw(msg);
            }
        }

        // Re-send dev callers if configured
        if (this.#devCallers.length > 0) {
            this.#sendRaw({ event: "dev.config", callers: this.#devCallers });
        }

        // Flush any other pending messages (skip channel.add and dev.config — already handled above)
        for (const msg of this.#pendingQueue) {
            if (msg.event === "channel.add" || msg.event === "dev.config") continue;
            this.#sendRaw(msg);
        }
        this.#pendingQueue = [];
    }

    /**
     * @internal The server acknowledged this agent (`agent.created`/`agent.resumed`).
     * Settles `ready` — this is the ONLY moment the agent exists server-side.
     */
    _markRegistered(): void {
        this.#registered = true;
        this.#readyResolve();
    }

    /**
     * @internal The socket dropped — the server no longer holds this
     * registration, so `ready` goes back to pending until the reconnect
     * re-registers us. Without this, a mint during a reconnect would race
     * against `agent.create` all over again.
     */
    _markUnregistered(): void {
        if (!this.#registered) return;
        this.#registered = false;
        this.#armReady();
    }

    /**
     * @internal The registration was terminally refused (see AgentConflictError).
     * Rejects `ready` so an awaiting caller fails loudly instead of hanging.
     */
    _failRegistration(err: Error): void {
        this.#readyReject(err);
    }
}

// ── Re-export ────────────────────────────────────────────────────────────

export { buildShortcutPayload } from "../protocol/shortcuts.js";
